/**
 * atomberg-support.js
 *
 * Support page tab switching and the customer-type → request-type → form
 * cascade for the "Service & Installation Request" tab. Deep-linkable via
 * query params: ?gettab=track-complaint|grievance-redressal|service-installation-request,
 * ?customer-type=customer|distributor|b2b|b2g|institutional,
 * ?request-type=service|installation|inspection (matches the reference's
 * own deep-link params).
 *
 * Routing table (customer type + request type → form snippet):
 *   customer      + service      → register-complaint
 *   customer      + installation → installation-form
 *   distributor   + inspection   → inspection-form
 *   b2b/b2g/institutional + service → b2b-service-form
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergSupportInit) return;
  w._atombergSupportInit = true;

  /** @type {Record<string, Array<{value: string, label: string}>>} */
  var REQUEST_TYPES_BY_CUSTOMER = {
    customer: [
      { value: 'service', label: 'Service Request' },
      { value: 'installation', label: 'Installation Request' },
    ],
    distributor: [{ value: 'inspection', label: 'Inspection Request' }],
    b2b: [{ value: 'service', label: 'Service Request' }],
    b2g: [{ value: 'service', label: 'Service Request' }],
    institutional: [{ value: 'service', label: 'Service Request' }],
  };

  /** @type {Record<string, string>} */
  var FORM_FOR = {
    'customer:service': 'register-complaint',
    'customer:installation': 'installation-form',
    'distributor:inspection': 'inspection-form',
    'b2b:service': 'b2b-service-form',
    'b2g:service': 'b2b-service-form',
    'institutional:service': 'b2b-service-form',
  };

  // Reference site renders a heading above the chosen form (its b2b label is
  // the dev artifact "ServiceForm3" — not copied).
  /** @type {Record<string, string>} */
  var FORM_HEADINGS = {
    'register-complaint': 'Service Request Form',
    'installation-form': 'Installation Request Form',
    'inspection-form': 'Inspection Request',
    'b2b-service-form': 'Service Request Form',
  };

  /** @type {Record<string, string>} */
  var TAB_QUERY_MAP = {
    'track-complaint': 'track-complaint',
    'grievance-redressal': 'grievance-redressal',
    'service-installation-request': 'service-installation-request',
  };

  /** @param {HTMLElement} root */
  function initTabs(root) {
    // The old site hosted Register Warranty as a TAB of this page, and QR
    // stickers/emails still link to it as ?gettab=register-warranty — that
    // flow lives on its own page in this theme, so forward there with every
    // other param intact (bg_serialnumber drives the warranty deep-link
    // prefill).
    var initialParams = new URLSearchParams(window.location.search);
    if ((initialParams.get('gettab') || '') === 'register-warranty') {
      var warrantyUrl =
        root.getAttribute('data-support-warranty-url') || '/pages/register-warranty';
      initialParams.delete('gettab');
      var qs = initialParams.toString();
      window.location.replace(
        warrantyUrl + (qs ? (warrantyUrl.indexOf('?') === -1 ? '?' : '&') + qs : '')
      );
      return;
    }

    var buttons = root.querySelectorAll('[data-support-tab-btn]');
    var panels = root.querySelectorAll('[data-support-tab-panel]');

    /** @param {string} tab */
    function showTab(tab) {
      buttons.forEach(function (btn) {
        var isActive = btn.getAttribute('data-support-tab-btn') === tab;
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        btn.classList.toggle('atomberg-support__tab-btn--active', isActive);
      });
      panels.forEach(function (panel) {
        /** @type {HTMLElement} */ (panel).hidden =
          panel.getAttribute('data-support-tab-panel') !== tab;
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tab = btn.getAttribute('data-support-tab-btn');
        if (tab) showTab(tab);
      });
    });

    var params = new URLSearchParams(window.location.search);
    var initialTab = TAB_QUERY_MAP[params.get('gettab') || ''] || 'service-installation-request';
    showTab(initialTab);
  }

  /** @param {HTMLElement} root */
  function initCascade(root) {
    var customerSelectEl = /** @type {HTMLSelectElement|null} */ (
      root.querySelector('[data-support-customer-type]')
    );
    var requestSelectEl = /** @type {HTMLSelectElement|null} */ (
      root.querySelector('[data-support-request-type]')
    );
    if (!customerSelectEl || !requestSelectEl) return;
    var customerSelect = customerSelectEl;
    var requestSelect = requestSelectEl;

    /** @param {string} customerType */
    function populateRequestTypes(customerType) {
      var options = REQUEST_TYPES_BY_CUSTOMER[customerType] || [];
      requestSelect.innerHTML = '';
      // Placeholder first — the reference site renders no form until the user
      // explicitly picks a request type, so nothing may be preselected here.
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select Type of Request';
      requestSelect.appendChild(placeholder);
      options.forEach(function (opt) {
        var el = document.createElement('option');
        el.value = opt.value;
        el.textContent = opt.label;
        requestSelect.appendChild(el);
      });
    }

    function showMatchingForm() {
      var key = customerSelect.value + ':' + requestSelect.value;
      var formName = FORM_FOR[key];
      root.querySelectorAll('[data-support-form]').forEach(function (el) {
        /** @type {HTMLElement} */ (el).hidden = el.getAttribute('data-support-form') !== formName;
      });
      var headingEl = /** @type {HTMLElement|null} */ (
        root.querySelector('[data-support-form-heading]')
      );
      if (headingEl) {
        headingEl.textContent = (formName && FORM_HEADINGS[formName]) || '';
        headingEl.hidden = !formName;
      }
    }

    function refilterAllScopes() {
      root.querySelectorAll('[data-support-scope]').forEach(function (el) {
        filterScopedSelects(/** @type {HTMLElement} */ (el));
      });
    }

    customerSelect.addEventListener('change', function () {
      populateRequestTypes(customerSelect.value);
      showMatchingForm();
      refilterAllScopes();
    });
    requestSelect.addEventListener('change', function () {
      showMatchingForm();
      refilterAllScopes();
    });

    var params = new URLSearchParams(window.location.search);
    // No default customer type — the reference starts both selects on their
    // placeholders, so nothing renders until the user chooses (deep links
    // still preselect).
    var initialCustomerType = params.get('customer-type') || '';
    if (!REQUEST_TYPES_BY_CUSTOMER[initialCustomerType]) initialCustomerType = '';
    customerSelect.value = initialCustomerType;
    populateRequestTypes(initialCustomerType);

    var initialRequestType = params.get('request-type');
    var availableValues = (REQUEST_TYPES_BY_CUSTOMER[initialCustomerType] || []).map(function (o) {
      return o.value;
    });
    if (initialRequestType && availableValues.indexOf(initialRequestType) !== -1) {
      requestSelect.value = initialRequestType;
    }
    showMatchingForm();
  }

  /**
   * The reference site only offers certain product types per customer-type +
   * request-type combination (e.g. installation is Fan/Smart Lock/Water
   * Purifier only, no Mixer) — mirrored here via the `contexts` list field on
   * support_product_type metaobject entries. Returns '' on pages without the
   * cascade (e.g. Register Warranty), which skips the filter entirely.
   * @returns {string}
   */
  function currentCascadeContext() {
    var customer = /** @type {HTMLSelectElement|null} */ (
      document.querySelector('[data-support-customer-type]')
    );
    var request = /** @type {HTMLSelectElement|null} */ (
      document.querySelector('[data-support-request-type]')
    );
    if (!customer || !request || !customer.value || !request.value) return '';
    return customer.value + ':' + request.value;
  }

  /**
   * Rebuilds a select to contain only the placeholder plus options passing
   * `matches`. Options are physically detached, NOT [hidden] — macOS native
   * select popups (Chrome and Safari) ignore hidden on <option>, showing
   * every choice regardless. The full option list is stashed on the element
   * on first call so filters can widen again later.
   * @param {HTMLSelectElement} select
   * @param {function(HTMLOptionElement): boolean} matches
   */
  function rebuildOptions(select, matches) {
    var anySelect = /** @type {any} */ (select);
    if (!anySelect._atombergAllOptions) {
      anySelect._atombergAllOptions = Array.from(select.options).filter(function (opt) {
        return !!opt.value;
      });
    }
    var all = /** @type {HTMLOptionElement[]} */ (anySelect._atombergAllOptions);
    var previous = select.value;
    Array.from(select.options).forEach(function (opt) {
      if (opt.value) opt.remove();
    });
    var hasSelected = false;
    all.forEach(function (opt) {
      if (!matches(opt)) return;
      select.appendChild(opt);
      if (opt.value === previous) hasSelected = true;
    });
    select.value = hasSelected ? previous : '';
    // A silent value wipe leaves downstream listeners (the Model search
    // clears its selection on category change) holding stale state — only
    // an actual change is announced, so this can't loop.
    if (select.value !== previous) {
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Filters <option data-product-type="..."> children of every
   * [data-support-scoped-by-type] select within `scope` (a row or a
   * customer-details block) to match the currently selected product type in
   * `scope`'s own [data-support-product-type] select. Used by
   * product-category and issue-category dropdowns, which are metaobject
   * lists scoped to a product type. Also filters the product-type options
   * themselves by the page-level cascade context (see
   * currentCascadeContext). Exposed globally so
   * atomberg-support-repeatable.js can re-run it on newly-cloned rows.
   * @param {HTMLElement} scope
   */
  function filterScopedSelects(scope) {
    var typeSelect = /** @type {HTMLSelectElement|null} */ (
      scope.querySelector('[data-support-product-type]')
    );
    if (!typeSelect) {
      // Repeatable rows carry no product-type select of their own — the
      // reference site picks ONE product type per submission (step 1 of the
      // installation form, top of step 2 for service), so rows inherit the
      // form-level selection.
      var formRoot = scope.closest('[data-support-form-root]');
      typeSelect = formRoot
        ? /** @type {HTMLSelectElement|null} */ (
            formRoot.querySelector('[data-support-product-type]')
          )
        : null;
    }
    if (!typeSelect) return;

    var context = currentCascadeContext();
    if (context) {
      var typeSelectRef = typeSelect;
      rebuildOptions(typeSelectRef, function (opt) {
        // Entries without a contexts value stay available everywhere so the
        // page keeps working against data seeded before this field existed.
        var contexts = opt.getAttribute('data-support-contexts') || '';
        return !contexts || contexts.split(',').indexOf(context) !== -1;
      });
    }

    var selectedType = typeSelect.value;
    scope.querySelectorAll('[data-support-scoped-by-type]').forEach(function (selectEl) {
      var select = /** @type {HTMLSelectElement} */ (selectEl);
      rebuildOptions(select, function (opt) {
        return opt.getAttribute('data-product-type') === selectedType;
      });
    });
  }

  document.addEventListener('change', function (e) {
    var target = /** @type {HTMLElement} */ (e.target);
    if (!target || !target.matches) return;

    if (target.matches('[data-support-product-type]')) {
      // Re-filter every scope in the form, not just the select's own — rows
      // inherit the form-level product type, so their category/issue selects
      // must react to this change too.
      var formRoot = target.closest('[data-support-form-root]');
      var scopeParent = formRoot || target.closest('[data-support-scope]');
      if (scopeParent) {
        var scopes = scopeParent.querySelectorAll('[data-support-scope]');
        if (scopes.length) {
          scopes.forEach(function (el) {
            filterScopedSelects(/** @type {HTMLElement} */ (el));
          });
        } else {
          filterScopedSelects(/** @type {HTMLElement} */ (scopeParent));
        }
      }
      return;
    }

    if (target.matches('[data-support-purchase-source]')) {
      var select = /** @type {HTMLSelectElement} */ (target);
      var field = select.closest('.atomberg-support-field');
      var otherWrap = field ? field.nextElementSibling : null;
      if (otherWrap && otherWrap.hasAttribute('data-support-purchase-source-other')) {
        var isOthers = select.value === 'others';
        /** @type {HTMLElement} */ (otherWrap).hidden = !isOthers;
        // Reference requires the free-text field once "Others" is picked.
        var otherInput = /** @type {HTMLTextAreaElement|null} */ (
          otherWrap.querySelector('textarea, input')
        );
        if (otherInput) otherInput.required = isOthers && select.required;
      }
    }
  });

  w.AtombergSupport = { filterScopedSelects: filterScopedSelects };

  function initAll() {
    document.querySelectorAll('.atomberg-support').forEach(function (el) {
      var root = /** @type {HTMLElement} */ (el);
      initTabs(root);
      initCascade(root);
    });
    document.querySelectorAll('[data-support-scope]').forEach(function (el) {
      filterScopedSelects(/** @type {HTMLElement} */ (el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
